import * as React from 'react';
import {
  Box,
  Collapse,
  Menu,
  MenuItem,
  MenuList,
  Popover,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useListProductsQuery } from 'renderer/features/products-page/services/products.endpoints';
import { DownloadMenuItem, getVisibleDownloadMenu } from '../downloadLinks';

const openExternal = {
  target: '_blank' as const,
  rel: 'noopener noreferrer',
};

type DesktopProps = {
  variant: 'desktop';
};

type MobileProps = {
  variant: 'mobile';
  onNavigate?: () => void;
};

type DownloadsNavProps = DesktopProps | MobileProps;

type MenuItemsProps = {
  items: DownloadMenuItem[];
};

const HOVER_BG = 'rgba(37, 37, 37, 0.08)';
const SELECTED_BG = 'rgba(37, 37, 37, 0.12)';
const SELECTED_HOVER_BG = 'rgba(37, 37, 37, 0.16)';

const desktopTriggerSx = {
  fontWeight: 900,
  textTransform: 'uppercase' as const,
  fontSize: '16px',
  px: 1,
  py: 0.5,
  borderRadius: '4px',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  color: 'white',
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  fontFamily: 'inherit',
  '&:hover': { textDecoration: 'underline' },
};

const menuItemSx = {
  '&:hover': { backgroundColor: HOVER_BG },
  '&.Mui-focusVisible': { backgroundColor: HOVER_BG },
  '&.Mui-selected': { backgroundColor: SELECTED_BG },
  '&.Mui-selected:hover': { backgroundColor: SELECTED_HOVER_BG },
};

type SubMenuState = {
  anchor: HTMLElement;
  item: DownloadMenuItem;
};

const DesktopDownloads = ({ items }: MenuItemsProps) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [subMenu, setSubMenu] = React.useState<SubMenuState | null>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
    setSubMenu(null);
  };

  const openSubMenu = (target: HTMLElement, item: DownloadMenuItem) => {
    if (item.children?.length) {
      setSubMenu({ anchor: target, item });
      return;
    }
    setSubMenu(null);
  };

  return (
    <>
      <Typography
        component="button"
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={(event) =>
          setAnchorEl((current) =>
            current ? null : (event.currentTarget as HTMLElement),
          )
        }
        sx={desktopTriggerSx}
      >
        Downloads
        <ArrowDropDownIcon fontSize="small" sx={{ ml: 0.25 }} />
      </Typography>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.label}
            component="a"
            href={item.href}
            {...openExternal}
            onClick={handleClose}
            onMouseEnter={(event) => openSubMenu(event.currentTarget, item)}
            selected={subMenu?.item.label === item.label}
            sx={{
              ...menuItemSx,
              justifyContent: 'space-between',
              minWidth: 260,
            }}
          >
            {item.label}
            {item.children?.length ? (
              <ChevronRightIcon fontSize="small" sx={{ ml: 2 }} />
            ) : null}
          </MenuItem>
        ))}
      </Menu>
      <Popover
        open={Boolean(subMenu?.item.children?.length)}
        anchorEl={subMenu?.anchor}
        onClose={() => setSubMenu(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        disableScrollLock
        hideBackdrop
        sx={{ pointerEvents: 'none' }}
        PaperProps={{
          sx: { pointerEvents: 'auto', ml: 0.25 },
        }}
      >
        <MenuList autoFocus={false}>
          {subMenu?.item.children?.map((child) => (
            <MenuItem
              key={child.href}
              component="a"
              href={child.href}
              {...openExternal}
              onClick={handleClose}
              sx={{ ...menuItemSx, fontSize: 14, minWidth: 220 }}
            >
              {child.label}
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  );
};

const MobileDownloads = ({
  onNavigate,
  items,
}: {
  onNavigate?: () => void;
} & MenuItemsProps) => {
  const [open, setOpen] = React.useState(false);
  const [expanded, setExpanded] = React.useState<string | null>(null);

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((value) => !value)}
        sx={{
          fontWeight: 900,
          textTransform: 'uppercase',
          fontSize: '18px',
          textAlign: 'center',
          py: 1,
          width: '100%',
          color: 'white',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'inherit',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
        }}
      >
        Downloads
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ pb: 1 }}>
          {items.map((item) => (
            <Box key={item.label}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'white',
                }}
              >
                <Box
                  component="a"
                  href={item.href}
                  {...openExternal}
                  onClick={onNavigate}
                  sx={{
                    flex: 1,
                    color: 'white',
                    textDecoration: 'none',
                    py: 0.75,
                    px: 2,
                    fontSize: 15,
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
                  }}
                >
                  {item.label}
                </Box>
                {item.children?.length ? (
                  <Box
                    component="button"
                    type="button"
                    aria-label={`Show ${item.label} hosts`}
                    onClick={() =>
                      setExpanded((current) =>
                        current === item.label ? null : item.label,
                      )
                    }
                    sx={{
                      color: 'white',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      px: 1,
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
                    }}
                  >
                    {expanded === item.label ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </Box>
                ) : null}
              </Box>
              {item.children?.length ? (
                <Collapse in={expanded === item.label}>
                  {item.children.map((child) => (
                    <Box
                      key={child.href}
                      component="a"
                      href={child.href}
                      {...openExternal}
                      onClick={onNavigate}
                      sx={{
                        display: 'block',
                        color: 'rgba(255,255,255,0.85)',
                        textDecoration: 'none',
                        py: 0.5,
                        pl: 4,
                        pr: 2,
                        fontSize: 14,
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.12)',
                          color: 'white',
                        },
                      }}
                    >
                      {child.label}
                    </Box>
                  ))}
                </Collapse>
              ) : null}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const DownloadsNav = ({ variant, ...rest }: DownloadsNavProps) => {
  const { data: products } = useListProductsQuery({ includeHidden: false });
  const items = React.useMemo(
    () => getVisibleDownloadMenu(products),
    [products],
  );

  if (variant === 'mobile') {
    return (
      <MobileDownloads
        items={items}
        onNavigate={'onNavigate' in rest ? rest.onNavigate : undefined}
      />
    );
  }
  return <DesktopDownloads items={items} />;
};

export default DownloadsNav;
